import { useMemo } from "react";

import {
    type CategoryFiltersConfig,
    filtersConfigStatusMap,
} from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";
import type { ICrmCustomFiltersListViewModel } from "app/pages/sales-intelligence/modules/crm-integration/types/filters-view-model";

import { createCrmFiltersConfig } from "../configs/crmFiltersConfig";
import { leadsCategories } from "../configs/filterCategory";
import useLeadsDescriptionQuery from "../queries/useLeadsDescriptionQuery";
import type { CrmIntegrationObject } from "../types/description.dto";
import { type LeadsCapability, leadsCapabilityMap } from "../types/leadsCapability";
import type { OrderedFilterInstance } from "../types/leadsSearchFilterInstance";

const EMPTY_INTEGRATIONS_LISTS: readonly CrmIntegrationObject[] = [];

type CrmFiltersConfig = CategoryFiltersConfig<OrderedFilterInstance>;

type ComputeCrmFiltersConfigOptions = {
    readonly grants: ReadonlySet<LeadsCapability>;
};

export const useComputeCrmFiltersConfig = (
    crmCustomFiltersListViewModel: ICrmCustomFiltersListViewModel,
    { grants }: ComputeCrmFiltersConfigOptions,
): CrmFiltersConfig => {
    const { data: description } = useLeadsDescriptionQuery();

    const userIntegrationsLists = description?.userIntegrationsLists;

    return useMemo((): CrmFiltersConfig => {
        const filtersList = createCrmFiltersConfig({
            grants,
            filtersListViewModel: crmCustomFiltersListViewModel,
            userIntegrationsLists: userIntegrationsLists ?? EMPTY_INTEGRATIONS_LISTS,
        });

        // CRM loads independently and renders its own inline loader via the category's
        // `emptyContent`. It must always report `ready` so a slow CRM
        // load never propagates `initial` into the merged status, which would blank the whole panel
        const status = filtersConfigStatusMap.ready;

        const isCategoryVisible = grants.has(leadsCapabilityMap.crmCustomFilters)
            ? crmCustomFiltersListViewModel.state !== "hidden"
            : grants.has(leadsCapabilityMap.crmLegacyFilter);

        const categoriesList = isCategoryVisible ? [leadsCategories.crm] : [];

        return { filtersList: filtersList, status, categoriesList };
    }, [crmCustomFiltersListViewModel, userIntegrationsLists, grants]);
};
